import { useRef, useState, useEffect } from 'react'
import { getConteudoChave, putConteudoChave, uploadImage, CONTEUDO_CHAVES } from '@/api/adminApi'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

export function AdminMenuPage() {
  const [bannerTexto, setBannerTexto] = useState('')
  const [bannerImagemUrl, setBannerImagemUrl] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const bannerFileInputRef = useRef<HTMLInputElement>(null)
  const logoFileInputRef = useRef<HTMLInputElement>(null)
  const { notifySaved, notifyError } = useSaveFeedback()

  useEffect(() => {
    let cancelled = false
    async function load() {
      setError(null)
      try {
        const [texto, url, logo] = await Promise.all([
          getConteudoChave(CONTEUDO_CHAVES.menu_banner_texto),
          getConteudoChave(CONTEUDO_CHAVES.menu_banner_imagem_url),
          getConteudoChave(CONTEUDO_CHAVES.app_logo_url).catch(() => null),
        ])
        if (!cancelled) {
          setBannerTexto(texto ?? '')
          setBannerImagemUrl(url ?? '')
          setLogoUrl(logo ?? '')
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erro ao carregar')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await Promise.all([
        putConteudoChave(CONTEUDO_CHAVES.menu_banner_texto, bannerTexto || null),
        putConteudoChave(CONTEUDO_CHAVES.menu_banner_imagem_url, bannerImagemUrl || null),
        putConteudoChave(CONTEUDO_CHAVES.app_logo_url, logoUrl || null),
      ])
      setSuccess('Alterações guardadas com sucesso.')
      notifySaved('Alterações guardadas com sucesso.')
    } catch (e) {
      notifyError(e, 'Erro ao salvar')
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    target: 'banner' | 'logo'
  ) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError(null)
    setUploading(true)
    try {
      const { url } = await uploadImage(file)
      if (target === 'banner') setBannerImagemUrl(url)
      if (target === 'logo') setLogoUrl(url)
      notifySaved('Imagem enviada.')
    } catch (e) {
      notifyError(e, 'Erro no upload')
      setUploadError(e instanceof Error ? e.message : 'Erro no upload.')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  if (loading) return <p className={styles.placeholderMsg}>A carregar…</p>

  return (
    <>
      <header className={styles.sectionHeader}>
        <h1 className={styles.sectionTitle}>Menu / Banner</h1>
        <p className={styles.sectionSubtitle}>
          Configure o texto e a imagem exibidos no banner da página inicial. O texto aparece como placeholder quando não houver imagem.
        </p>
      </header>
      {error && (
        <div className={styles.erroSection}>
          <p className={styles.erro}>{error}</p>
        </div>
      )}
      {success && (
        <div className={styles.successSection}>
          <p className={styles.successMsg}>{success}</p>
        </div>
      )}
      <div className={styles.sectionCard}>
        <form onSubmit={handleSalvar}>
          <div className={styles.formGroup}>
            <label>Texto do banner (placeholder)</label>
            <input
              type="text"
              value={bannerTexto}
              onChange={(e) => setBannerTexto(e.target.value)}
              placeholder="Ex: Banner / Imagem"
            />
          </div>
          <div className={styles.formGroup}>
            <label>Logo do app</label>
            <div className={styles.uploadStack}>
              <div className={styles.uploadRow}>
                <input
                  ref={logoFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => handleFileChange(e, 'logo')}
                  className={styles.uploadInputHidden}
                  aria-label="Fazer upload de logo"
                />
                <button
                  type="button"
                  className={styles.btnUpload}
                  onClick={() => logoFileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'A enviar…' : logoUrl ? 'Trocar logo' : 'Carregar logo'}
                </button>
                {logoUrl && (
                  <button
                    type="button"
                    className={styles.btnCancel}
                    onClick={() => setLogoUrl('')}
                    disabled={uploading}
                  >
                    Remover
                  </button>
                )}
              </div>
              {logoUrl ? (
                <div className={styles.bannerPreview}>
                  <img src={resolveMediaUrl(logoUrl)} alt="Logo do app" />
                </div>
              ) : (
                <p className={styles.uploadEmptyHint}>Sem logo carregada.</p>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Imagem do banner</label>
            <div className={styles.uploadStack}>
              <div className={styles.uploadRow}>
                <input
                  ref={bannerFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => handleFileChange(e, 'banner')}
                  className={styles.uploadInputHidden}
                  aria-label="Fazer upload de imagem"
                />
                <button
                  type="button"
                  className={styles.btnUpload}
                  onClick={() => bannerFileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'A enviar…' : bannerImagemUrl ? 'Trocar imagem' : 'Carregar imagem'}
                </button>
                {bannerImagemUrl && (
                  <button
                    type="button"
                    className={styles.btnCancel}
                    onClick={() => setBannerImagemUrl('')}
                    disabled={uploading}
                  >
                    Remover
                  </button>
                )}
              </div>
              {uploadError && <p className={styles.uploadError}>{uploadError}</p>}
              {bannerImagemUrl ? (
                <div className={styles.bannerPreview}>
                  <img src={resolveMediaUrl(bannerImagemUrl)} alt="Banner" />
                </div>
              ) : (
                <p className={styles.uploadEmptyHint}>Sem banner carregado.</p>
              )}
            </div>
          </div>
          <button type="submit" className={styles.btnSalvar} disabled={saving}>
            {saving ? 'A guardar…' : 'Salvar'}
          </button>
        </form>
      </div>
    </>
  )
}
